import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { getMappedProductType } from 'config'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { setCachedCreditReportDefaults } from 'pages/LoanApplication/CreditScore/determine-credit-score'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import {
  CreditReportDefaultsConfig,
  CreditScoreSelectionMethod,
  defaultCreditReportDefaultsConfig,
  productTypeSelectInputs,
  ruleInputs,
} from './constants'

export const CreditReportDefaultsConfigPage = () => {
  const [isLoading, setLoading] = useState(false)
  const [isChanged, setChanged] = useState(false)
  const [config, setConfig] = useState<CreditReportDefaultsConfig>(defaultCreditReportDefaultsConfig())
  const { optionsMap, fieldProperties: loanStructureFields } = useProductTypeOptions()
  const productInputs = productTypeSelectInputs(optionsMap)
  const rulesFieldInputs = ruleInputs()

  useEffect(() => {
    setLoading(true)
    getAdminConfig('creditReportDefaults')
      .then((value) => {
        const defaults = defaultCreditReportDefaultsConfig()
        const next = {
          byProductType: { ...defaults.byProductType, ...(value?.byProductType || {}) },
          rules: { ...defaults.rules, ...(value?.rules || {}) },
        }
        setConfig(next)
        setCachedCreditReportDefaults(next)
      })
      .finally(() => setLoading(false))
  }, [])

  const onChangeProductMethod = (productType: string, value: CreditScoreSelectionMethod) => {
    const next = cloneDeep(config)
    next.byProductType[productType] = value
    setConfig(next)
    setChanged(true)
  }

  const onChangeRule = (key: string, value: boolean) => {
    const next = cloneDeep(config)
    ;(next.rules as any)[key] = value
    setConfig(next)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig('creditReportDefaults', config)
    setCachedCreditReportDefaults(config)
    setLoading(false)
    setChanged(false)
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />

      <p className="text-sm text-gray-600 mb-4">
        Select how the loan Estimated Credit Score is calculated from borrower bureau scores for each product type.
      </p>

      <p className="text-md font-bold border-b mb-3 pb-1">Score Selection by Product Type</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        {Object.keys(optionsMap).map((productType) => {
          const input: any = {
            ...productInputs[productType],
            value:
              config.byProductType[productType] ||
              config.byProductType[getMappedProductType(productType, loanStructureFields)] ||
              CreditScoreSelectionMethod.LowestMidScore,
          }
          return (
            <div key={productType} className={`input md:col-span-${input.span || 1}`}>
              <RenderInput
                input={input}
                Key={productType}
                onChange={(_: string, value: CreditScoreSelectionMethod) => onChangeProductMethod(productType, value)}
              />
            </div>
          )
        })}
      </div>

      <p className="text-md font-bold border-b mb-3 pb-1">Additional Mid Score Rules</p>
      <div className="grid grid-cols-1 gap-2 mb-6">
        {Object.keys(rulesFieldInputs).map((key) => {
          const input: any = {
            ...rulesFieldInputs[key],
            value: !!(config.rules as any)[key],
          }
          return (
            <div key={key} className="input">
              <RenderInput input={input} Key={key} onChange={(_: string, value: boolean) => onChangeRule(key, value)} />
            </div>
          )
        })}
      </div>

      <Button disabled={!isChanged} onClick={onSubmit}>
        Save
      </Button>
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
